import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, object, string, number, mixed } from 'yup';
import * as yup from 'yup';

export type UpdateMinesProviderConfigFormState = {
  maxWin: number;
  defaultBet: number;
  minBet: number;
  maxBet: number;
  isRoundable: string | boolean;
  currency: string;
};

export const updateMinesProviderConfigFormStateDefault: UpdateMinesProviderConfigFormState = {
  maxWin: 0,
  defaultBet: 0,
  minBet: 0,
  maxBet: 0,
  isRoundable: '',
  currency: '',
};

export const updateMinesProviderConfigSchemaDefault: ObjectSchema<
  Partial<UpdateMinesProviderConfigFormState>
> = object<Partial<UpdateMinesProviderConfigFormState>>().shape({
  maxWin: number().required('Max Win is required').positive('Max Win must be a positive number'),
  defaultBet: number()
    .required('Default Bet is required')
    .positive('Default Bet must be a positive number'),
  minBet: number()
    .required('Min Bet is required')
    .positive('Min Bet must be a positive number')
    .max(yup.ref('maxBet'), 'Min Bet must be less than Max Bet'),
  maxBet: number()
    .required('Max Bet is required')
    .positive('Max Bet must be a positive number')
    .min(yup.ref('minBet'), 'Max Bet must be greater than Min Bet'),
  isRoundable: string().required('Boolean is required'),
  currency: string()
    .required('Currency is required')
    .min(3, 'Currency must be at least 3 characters long'),
});

type UpdateMinesProviderConfigFormContextType = {
  schema: ObjectSchema<Partial<UpdateMinesProviderConfigFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<UpdateMinesProviderConfigFormState>>>;
  methods?: UseFormReturn<UpdateMinesProviderConfigFormState>;
};

const updateMinesProviderConfigFormContextDefault = {
  schema: updateMinesProviderConfigSchemaDefault,
  setSchema: noop,
} satisfies UpdateMinesProviderConfigFormContextType;

export const UpdateMinesProviderConfigFormContext =
  createContext<UpdateMinesProviderConfigFormContextType>(
    updateMinesProviderConfigFormContextDefault
  );

export const useUpdateMinesProviderConfigForm = () => {
  const { methods } = useContext(UpdateMinesProviderConfigFormContext);
  if (!methods) {
    throw new Error('UpdateMinesProviderConfigFormContext is empty');
  }

  return methods;
};

export const useManageUpdateMinesProviderConfigSchema = () => {
  const { setSchema } = useContext(UpdateMinesProviderConfigFormContext);

  return { setSchema };
};
